/**
 * @fileoverview Email categories and the one check every sender runs before
 * emailing a user: {@link mayEmailForCategory}.
 *
 * The preferences live on `users/{uid}`. Only an explicit `false` turns a
 * category off; an absent field (or any non-boolean) means the person never
 * said no. `okToEmail` is the master switch for the optional categories: it
 * is `false` exactly when every optional category is off, so a sender that
 * still reads only `okToEmail` stays right for people who turned everything
 * off.
 */

import {z} from 'zod';

/** Every kind of email a course sends. Each send names exactly one. */
export const EMAIL_CATEGORIES = [
  'essential',
  'catchUpReminders',
  'courseNews',
  'sectionReminders',
  'forumReplies',
  'futureCourseNews',
] as const;

/** One kind of email a course sends. See {@link EMAIL_CATEGORIES}. */
export type EmailCategory = (typeof EMAIL_CATEGORIES)[number];

/** The categories a person can turn off, in the order the page lists them. */
export const OPTIONAL_EMAIL_CATEGORIES = [
  'catchUpReminders',
  'courseNews',
  'sectionReminders',
  'forumReplies',
  'futureCourseNews',
] as const satisfies readonly EmailCategory[];

/** A category a person can turn off. */
export type OptionalEmailCategory = (typeof OPTIONAL_EMAIL_CATEGORIES)[number];

/**
 * The `users/{uid}` field that holds each optional category's switch. Forum
 * replies and section reminders keep the fields they had before categories.
 */
export const EMAIL_PREFERENCE_FIELDS = {
  catchUpReminders: 'okToSendCatchUpReminders',
  courseNews: 'okToSendCourseNews',
  sectionReminders: 'okToSendSectionReminders',
  forumReplies: 'okToSendNotifications',
  futureCourseNews: 'okToSendFutureCourseNews',
} as const satisfies Record<OptionalEmailCategory, string>;

/** A `users/{uid}` field that holds one optional category's switch. */
export type EmailPreferenceField =
  (typeof EMAIL_PREFERENCE_FIELDS)[OptionalEmailCategory];

/** A stored switch: a boolean, or `undefined` for absent or unreadable. */
const storedSwitch = z.boolean().optional().catch(undefined);

/**
 * The email-preference fields of a `users/{uid}` doc. Non-boolean values
 * read as `undefined`, which means yes. Other fields pass through.
 */
export const EmailPreferencesSchema = z
  .object({
    /** Master switch for every optional category. */
    okToEmail: storedSwitch,
    /** Catch-up reminders for work not yet done. */
    okToSendCatchUpReminders: storedSwitch,
    /** Celebrations and course news. */
    okToSendCourseNews: storedSwitch,
    /** Reminders before a person's section meets. */
    okToSendSectionReminders: storedSwitch,
    /** Replies in the course forum. */
    okToSendNotifications: storedSwitch,
    /** News about future courses. */
    okToSendFutureCourseNews: storedSwitch,
    /**
     * When the person last saved the preferences page (server time). Its
     * presence means they have seen that course essentials always come.
     */
    emailPreferencesSavedAt: z.unknown().optional(),
  })
  .loose();

/** The parsed email-preference fields of a `users/{uid}` doc. */
export type EmailPreferences = z.infer<typeof EmailPreferencesSchema>;

/**
 * Whether an essential email skips someone who set `okToEmail: false` before
 * the preferences page existed and has not saved it since. Those people
 * turned off "important emails" and were never told essentials still come.
 */
export const ESSENTIALS_HONOR_LEGACY_OPT_OUT = true;

/**
 * Reads the email-preference fields from a raw `users/{uid}` doc.
 *
 * @param userDoc - The doc's data, or `undefined` when there is no doc.
 * @return The parsed fields; every switch is `undefined` when the doc is
 *     absent or not an object.
 * @throws Never.
 */
export function parseEmailPreferences(userDoc: unknown): EmailPreferences {
  const parsed = EmailPreferencesSchema.safeParse(userDoc ?? {});
  return parsed.success ? parsed.data : {};
}

/**
 * Whether one optional category is on: the master switch and the category's
 * own switch are both not `false`.
 *
 * @param params.preferences - Parsed preferences, from
 *     {@link parseEmailPreferences}.
 * @param params.category - The category to read.
 * @return `true` when the person may get this category.
 * @throws Never.
 */
export function isEmailCategoryOn({
  preferences,
  category,
}: {
  preferences: EmailPreferences;
  category: OptionalEmailCategory;
}): boolean {
  return (
    preferences.okToEmail !== false &&
    preferences[EMAIL_PREFERENCE_FIELDS[category]] !== false
  );
}

/**
 * The one check every sender runs before emailing a user. Essential email
 * always goes, except to a legacy opt-out while
 * {@link ESSENTIALS_HONOR_LEGACY_OPT_OUT} is set; any other category goes
 * only when {@link isEmailCategoryOn}.
 *
 * @param params.userDoc - The recipient's raw `users/{uid}` data, or
 *     `undefined` when there is no doc (which means yes to everything).
 * @param params.category - What kind of email this is.
 * @param params.essentialsHonorLegacyOptOut - Overrides
 *     {@link ESSENTIALS_HONOR_LEGACY_OPT_OUT}; omit it to use that flag.
 * @return `true` when the email may be sent.
 * @throws Never.
 */
export function mayEmailForCategory({
  userDoc,
  category,
  essentialsHonorLegacyOptOut = ESSENTIALS_HONOR_LEGACY_OPT_OUT,
}: {
  userDoc: unknown;
  category: EmailCategory;
  essentialsHonorLegacyOptOut?: boolean;
}): boolean {
  const preferences = parseEmailPreferences(userDoc);
  if (category !== 'essential') {
    return isEmailCategoryOn({preferences, category});
  }
  const isLegacyOptOut =
    preferences.okToEmail === false &&
    preferences.emailPreferencesSavedAt === undefined;
  return !(essentialsHonorLegacyOptOut && isLegacyOptOut);
}
